import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { EvalPdrSandbox } from "../components/EvalPdrSandbox";
import {
  EvalResultsLoading,
  EvalResultsTable,
  EvalRunProgress,
  EvalTableExpandToggle,
} from "../components/EvalResultsTable";
import { EvalRunHistoryPanel } from "../components/EvalRunHistoryPanel";
import { Icon } from "../components/Icon";
import { PageTitle } from "../components/PageTitle";
import { SynthesisPromptEditor } from "../components/SynthesisPromptEditor";
import { VendorClusterPicker } from "../components/VendorClusterPicker";
import { Button, Drawer, Select } from "../components/ui";
import { useToast } from "../components/Toast";
import { apiGet } from "../lib/api";
import {
  deleteEvalRunRemote,
  fetchEvalClusterDocuments,
  fetchEvalClusters,
  fetchEvalJob,
  fetchEvalRunDetail,
  fetchEvalRuns,
  fetchEvalSandboxPdrs,
  persistEvalSandboxPdr,
  startEvalRun,
} from "../lib/evalPlaygroundApi";
import {
  deleteEvalRun,
  getEvalRun,
  loadEvalHistory,
  loadEvalSandboxPdrStore,
  loadEvalPrefs,
  loadEvalPromptSandbox,
  restoredEvalModels,
  saveEvalPrefs,
  saveEvalPromptSandbox,
  saveEvalStandalonePdr,
  sandboxTextsFromStore,
  upsertEvalRun,
  type EvalCluster,
  type EvalClusterDocument,
  type EvalCoverage,
  type EvalEvidencePreview,
  type EvalResultSnapshot,
  type SavedEvalRun,
} from "../lib/evalPlaygroundStorage";
import {
  emptyPdrJsonSkeleton,
  fieldKeysForProfile,
  normalizePdrProfile,
  PDR_PROFILE_COMPLETE,
  PDR_PROFILE_OPTIONS,
  PDR_PROFILE_SLIM,
  type PdrSchemaProfile,
} from "../lib/pdrFields";
import {
  fetchSynthesisPrompts,
  missingTokens,
  type SynthesisPromptTemplate,
} from "../lib/synthesisPromptsApi";
import type { OpenRouterModel } from "../lib/types";
import { formatApiError } from "../lib/utils";

type EvalResponse = {
  run_id?: string;
  evidence_id: string;
  project_id?: string | null;
  include_reasoning: boolean;
  pdr_schema_profile?: string;
  pdr_context?: string | null;
  evidence_preview?: EvalEvidencePreview;
  project_preview?: { project_name?: string; target_company?: string; vendor_name?: string };
  results: EvalResultSnapshot[];
};

type ProjectOption = {
  project_id: string;
  project_name: string;
  target_company: string;
  vendor_name: string;
};

/** Runs are background jobs (a chain of calls can outlast any request); poll their progress. */
const EVAL_POLL_MS = 4_000;
const EVAL_POLL_MAX_FAILURES = 5;
const EVAL_JOB_FAILED = new Set(["error", "interrupted", "cancelled", "budget_exceeded"]);

function coverageText(coverage?: EvalCoverage): string {
  const total = coverage?.documents_total ?? 0;
  if (!total) return "";
  const calls = coverage?.calls_per_model ?? 1;
  const parts = [
    `${coverage?.documents_read ?? 0} of ${total} document${total === 1 ? "" : "s"} read`,
  ];
  if (coverage?.documents_partial) parts.push(`${coverage.documents_partial} partly`);
  parts.push(`${calls} call${calls === 1 ? "" : "s"} per model`);
  return parts.join(" · ");
}

function modelLabel(modelId: string, catalog: OpenRouterModel[]): string {
  const hit = catalog.find((m) => m.id === modelId);
  return hit?.name || modelId.split("/").pop() || modelId;
}

function savedRunToResponse(run: SavedEvalRun): EvalResponse {
  return {
    evidence_id: run.evidence_id,
    project_id: run.project_id || "",
    include_reasoning: true,
    evidence_preview: run.evidence_preview,
    project_preview: run.project_preview,
    results: run.results,
  };
}

/** Eval playground always uses audit reasoning (same as Phase 4 default). */
const EVAL_INCLUDE_REASONING = true;

function emptyPdrByProfile(): Record<PdrSchemaProfile, string> {
  return { slim: "", complete: "" };
}

function formatRunWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function EvalPlaygroundPage() {
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const clusterParam = params.get("cluster") || "";
  const prefsHydrated = useRef(false);
  const pdrHydrated = useRef(false);

  const [bucketId, setBucketId] = useState(clusterParam);
  const [clusters, setClusters] = useState<EvalCluster[]>([]);
  const [clustersLoading, setClustersLoading] = useState(true);
  const [clusterInfo, setClusterInfo] = useState<EvalCluster | null>(null);
  const [clusterDocs, setClusterDocs] = useState<EvalClusterDocument[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [checkedDocIds, setCheckedDocIds] = useState<string[]>([]);
  /** Ticks to apply once a cluster's documents arrive (restoring a saved run). */
  const pendingChecked = useRef<string[] | null>(null);
  const docsLoadGen = useRef(0);
  const [maxCallsPerModel, setMaxCallsPerModel] = useState(4);
  const [importProjectId, setImportProjectId] = useState("");
  const [catalog, setCatalog] = useState<OpenRouterModel[]>([]);
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [defaultModels, setDefaultModels] = useState<string[]>([]);
  const [pickerModel, setPickerModel] = useState("");
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  /** Background evaluation being polled; empty when no run is in flight. */
  const [runningJobId, setRunningJobId] = useState("");
  const [jobModels, setJobModels] = useState<string[]>([]);
  const [partialResults, setPartialResults] = useState<EvalResultSnapshot[]>([]);
  const [runStartedAt, setRunStartedAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const running = Boolean(runningJobId);
  const [response, setResponse] = useState<EvalResponse | null>(null);
  const [history, setHistory] = useState<SavedEvalRun[]>(() => loadEvalHistory());
  const [historyLoading, setHistoryLoading] = useState(false);
  const [activeRunId, setActiveRunId] = useState("");
  const [runsPanelOpen, setRunsPanelOpen] = useState(false);
  const [resultsFocusMode, setResultsFocusMode] = useState(false);
  const [tableExpanded, setTableExpanded] = useState(false);
  const [pdrSentOpen, setPdrSentOpen] = useState(false);
  const [pdrByProfile, setPdrByProfile] = useState(emptyPdrByProfile);
  const [pdrBaselineByProfile, setPdrBaselineByProfile] = useState(emptyPdrByProfile);
  const [pdrLoading, setPdrLoading] = useState(false);
  const [pdrCollapsed, setPdrCollapsed] = useState(true);
  const [pdrSchemaProfile, setPdrSchemaProfile] = useState<PdrSchemaProfile>("slim");
  const pdrLoadGen = useRef(0);
  const [promptTemplates, setPromptTemplates] = useState<SynthesisPromptTemplate[]>([]);
  /** Prompt kinds an eval run sends (from /defaults); production's batch prompts are not among them. */
  const [evalPromptKinds, setEvalPromptKinds] = useState<string[] | null>(null);
  const [promptLoading, setPromptLoading] = useState(true);
  /** This browser's edits to the synthesis prompt; sent with runs, never saved by a run. */
  const [promptSandbox, setPromptSandbox] = useState<Record<string, string>>(() =>
    loadEvalPromptSandbox(),
  );
  const [promptCollapsed, setPromptCollapsed] = useState(true);
  const evalPromptTemplates = useMemo(
    () =>
      evalPromptKinds
        ? promptTemplates.filter((t) => evalPromptKinds.includes(t.kind))
        : promptTemplates,
    [promptTemplates, evalPromptKinds],
  );
  /** Sandbox edits a run can use; an edit left on a batch prompt is never sent (the server refuses it). */
  const evalSandbox = useMemo(
    () =>
      evalPromptKinds
        ? Object.fromEntries(
            Object.entries(promptSandbox).filter(([kind]) => evalPromptKinds.includes(kind)),
          )
        : promptSandbox,
    [promptSandbox, evalPromptKinds],
  );
  const updatePromptSandbox = useCallback((next: Record<string, string>) => {
    setPromptSandbox(next);
    saveEvalPromptSandbox(next);
  }, []);

  const availableToAdd = useMemo(
    () => catalog.filter((m) => m.id && !selectedModels.includes(m.id)),
    [catalog, selectedModels],
  );

  const activeModels = useMemo(
    () => selectedModels.filter(Boolean),
    [selectedModels],
  );

  const persistPrefs = useCallback(
    (overrides?: Partial<{
      models: string[];
      lastRunId: string;
      resultsFocusMode?: boolean;
      tableExpanded?: boolean;
      runningJobId?: string;
    }>) => {
      saveEvalPrefs({
        selectedModels: overrides?.models ?? selectedModels,
        defaultModels,
        bucketId,
        runningJobId: overrides?.runningJobId ?? runningJobId,
        includeReasoning: EVAL_INCLUDE_REASONING,
        lastRunId: overrides?.lastRunId ?? activeRunId,
        pdrCollapsed,
        pdrSchemaProfile,
        resultsFocusMode: overrides?.resultsFocusMode ?? resultsFocusMode,
        tableExpanded: overrides?.tableExpanded ?? tableExpanded,
      });
    },
    [
      selectedModels,
      defaultModels,
      bucketId,
      runningJobId,
      activeRunId,
      pdrCollapsed,
      pdrSchemaProfile,
      resultsFocusMode,
      tableExpanded,
    ],
  );

  const toggleTableExpanded = useCallback(() => {
    setTableExpanded((prev) => {
      const next = !prev;
      persistPrefs({ tableExpanded: next });
      return next;
    });
  }, [persistPrefs]);

  const activeSavedRun = useMemo(
    () => (activeRunId ? history.find((h) => h.id === activeRunId) : undefined),
    [activeRunId, history],
  );

  const pdrContextText = pdrByProfile[pdrSchemaProfile];
  const pdrDirty =
    pdrByProfile[pdrSchemaProfile].trim() !== pdrBaselineByProfile[pdrSchemaProfile].trim();

  const activePdrContext = activeSavedRun?.pdr_context?.trim() || pdrContextText.trim();

  /** Schema used for the run being displayed (not necessarily the active sandbox tab). */
  const resultsSchemaProfile = useMemo((): PdrSchemaProfile => {
    if (activeSavedRun?.pdr_schema_profile) {
      return normalizePdrProfile(activeSavedRun.pdr_schema_profile);
    }
    if (response?.pdr_schema_profile) {
      return normalizePdrProfile(response.pdr_schema_profile);
    }
    return pdrSchemaProfile;
  }, [activeSavedRun, response?.pdr_schema_profile, pdrSchemaProfile]);

  const schemaMismatch =
    Boolean(response?.results?.length) && resultsSchemaProfile !== pdrSchemaProfile;

  const importProjectOptions = useMemo(
    () =>
      projects.map((p) => ({
        project_id: p.project_id,
        label: `${p.project_name} · ${p.target_company}`,
      })),
    [projects],
  );

  const applyPdrText = useCallback(
    (profile: PdrSchemaProfile, text: string, opts?: { saved?: boolean }) => {
      setPdrByProfile((prev) => ({ ...prev, [profile]: text }));
      if (opts?.saved) {
        setPdrBaselineByProfile((prev) => ({ ...prev, [profile]: text }));
      }
    },
    [],
  );

  const hydrateStandalonePdr = useCallback(async () => {
    const gen = ++pdrLoadGen.current;
    setPdrLoading(true);
    try {
      const store = loadEvalSandboxPdrStore();
      const localTexts = sandboxTextsFromStore(store);
      const remote = await fetchEvalSandboxPdrs();
      const merged: Record<PdrSchemaProfile, string> = {
        slim: localTexts.slim.trim() || remote.slim || "",
        complete: localTexts.complete.trim() || remote.complete || "",
      };
      if (gen !== pdrLoadGen.current) return;
      const profiles: PdrSchemaProfile[] = [PDR_PROFILE_SLIM, PDR_PROFILE_COMPLETE];
      for (const prof of profiles) {
        const text = merged[prof];
        const localEntry = store[prof];
        const saved = Boolean(localEntry?.explicitly_saved && localTexts[prof].trim());
        applyPdrText(prof, text, { saved });
        if (text && !localTexts[prof].trim() && remote[prof]?.trim()) {
          saveEvalStandalonePdr(prof, text, { explicitlySaved: true });
          setPdrBaselineByProfile((prev) => ({ ...prev, [prof]: text }));
        }
      }
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      if (gen === pdrLoadGen.current) setPdrLoading(false);
    }
  }, [applyPdrText, toast]);

  const handlePdrContextChange = (text: string) => {
    setPdrByProfile((prev) => ({ ...prev, [pdrSchemaProfile]: text }));
    saveEvalStandalonePdr(pdrSchemaProfile, text, { explicitlySaved: false });
  };

  const importPdrFromProject = async () => {
    const pid = importProjectId.trim();
    if (!pid) return;
    const gen = ++pdrLoadGen.current;
    setPdrLoading(true);
    try {
      const q = new URLSearchParams({
        project_id: pid,
        include_reasoning: String(EVAL_INCLUDE_REASONING),
      });
      const data = await apiGet<{ pdr_context: string }>(
        `/api/evaluate-synthesis/project-state?${q}`,
      );
      if (gen !== pdrLoadGen.current) return;
      const formatted = data.pdr_context || "";
      applyPdrText(pdrSchemaProfile, formatted);
      saveEvalStandalonePdr(pdrSchemaProfile, formatted, { explicitlySaved: false });
      toast("Imported project PDR into eval sandbox (edit and Save PDR to keep)");
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      if (gen === pdrLoadGen.current) setPdrLoading(false);
    }
  };

  const persistPdrProfile = (profile: PdrSchemaProfile) => {
    setPdrSchemaProfile(profile);
    persistPrefs();
  };

  const insertPdrSkeleton = () => {
    const json = emptyPdrJsonSkeleton(pdrSchemaProfile, true);
    handlePdrContextChange(json);
    toast(
      `Inserted empty ${pdrSchemaProfile} JSON (${fieldKeysForProfile(pdrSchemaProfile).length} fields) — save, then run`,
    );
  };

  const saveSandboxPdr = () => {
    const text = pdrByProfile[pdrSchemaProfile].trim();
    if (!text) {
      toast("PDR context is empty", true);
      return;
    }
    saveEvalStandalonePdr(pdrSchemaProfile, text, { explicitlySaved: true });
    void persistEvalSandboxPdr(pdrSchemaProfile, text);
    setPdrBaselineByProfile((prev) => ({ ...prev, [pdrSchemaProfile]: text }));
    toast(`Eval PDR (${pdrSchemaProfile}) saved for future runs`);
  };

  const loadDefaults = useCallback(async () => {
    const saved = loadEvalPrefs();
    const data = await apiGet<{
      default_models: string[];
      max_calls_per_model?: number;
      prompt_kinds?: string[];
      models: OpenRouterModel[];
      pdr_schema_profile?: string;
    }>("/api/evaluate-synthesis/defaults");
    const models = data.models || [];
    setCatalog(models);
    if (data.max_calls_per_model) setMaxCallsPerModel(data.max_calls_per_model);
    if (data.prompt_kinds?.length) setEvalPromptKinds(data.prompt_kinds);
    const apiDefaults = data.default_models?.length
      ? data.default_models
      : [
          "google/gemini-3-flash-preview",
          "anthropic/claude-sonnet-5",
          "openai/gpt-6-sol",
          "x-ai/grok-4.7",
          "deepseek/deepseek-v4.1-flash",
        ];
    setDefaultModels(apiDefaults);

    if (!prefsHydrated.current) {
      prefsHydrated.current = true;
      if (saved.bucketId && !clusterParam) setBucketId(saved.bucketId);
      if (saved.runningJobId) setRunningJobId(saved.runningJobId);
      if (typeof saved.pdrCollapsed === "boolean") {
        setPdrCollapsed(saved.pdrCollapsed);
      }
      if (typeof saved.resultsFocusMode === "boolean") {
        setResultsFocusMode(saved.resultsFocusMode);
      }
      if (typeof saved.tableExpanded === "boolean") {
        setTableExpanded(saved.tableExpanded);
      }
      if (saved.pdrSchemaProfile) {
        setPdrSchemaProfile(normalizePdrProfile(saved.pdrSchemaProfile));
      } else if (data.pdr_schema_profile) {
        setPdrSchemaProfile(normalizePdrProfile(data.pdr_schema_profile));
      }
      setSelectedModels(restoredEvalModels(saved, apiDefaults));
      if (saved.lastRunId) {
        const run = getEvalRun(saved.lastRunId);
        if (run) {
          const hydrate = (full: SavedEvalRun) => {
            setActiveRunId(full.id);
            if (full.results?.length) setResponse(savedRunToResponse(full));
            const prof = normalizePdrProfile(full.pdr_schema_profile || pdrSchemaProfile);
            if (full.pdr_schema_profile) {
              setPdrSchemaProfile(prof);
            }
            if (full.pdr_context?.trim()) {
              applyPdrText(prof, full.pdr_context, { saved: true });
            } else if (full.existing_pdr) {
              applyPdrText(prof, JSON.stringify(full.existing_pdr, null, 2), { saved: true });
            }
          };
          if (run.results?.length) {
            hydrate(run);
          } else {
            fetchEvalRunDetail(run.id)
              .then(hydrate)
              .catch(() => hydrate(run));
          }
        }
      }
    } else {
      setSelectedModels((prev) => (prev.length ? prev : apiDefaults));
    }
  }, [clusterParam, applyPdrText]);

  const loadProjects = useCallback(async () => {
    const data = await apiGet<{ projects: ProjectOption[] }>("/api/projects?limit=200");
    setProjects(data.projects || []);
  }, []);

  const loadClusters = useCallback(async () => {
    setClustersLoading(true);
    try {
      setClusters(await fetchEvalClusters());
    } finally {
      setClustersLoading(false);
    }
  }, []);

  /** Pick a cluster; `checked` restores a saved run's ticks, otherwise every document is ticked. */
  const selectCluster = useCallback(
    (id: string, checked?: string[]) => {
      pendingChecked.current = checked ?? null;
      if (id === bucketId && checked) {
        pendingChecked.current = null;
        setCheckedDocIds(
          clusterDocs.filter((d) => checked.includes(d.evidence_id)).map((d) => d.evidence_id),
        );
        return;
      }
      setBucketId(id);
      setParams(id ? { cluster: id } : {}, { replace: true });
    },
    [bucketId, clusterDocs, setParams],
  );

  const refreshHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const runs = await fetchEvalRuns(50);
      setHistory(runs);
      return runs;
    } catch {
      const local = loadEvalHistory();
      setHistory(local);
      return local;
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDefaults().catch((e) => toast(formatApiError(e), true));
    loadProjects().catch((e) => toast(formatApiError(e), true));
    if (!pdrHydrated.current) {
      pdrHydrated.current = true;
      void hydrateStandalonePdr();
    }
    void refreshHistory();
    loadClusters().catch((e) => toast(formatApiError(e), true));
    fetchSynthesisPrompts()
      .then(setPromptTemplates)
      .catch((e) => toast(formatApiError(e), true))
      .finally(() => setPromptLoading(false));
  }, []);

  useEffect(() => {
    if (clusterParam) setBucketId(clusterParam);
  }, [clusterParam]);

  useEffect(() => {
    const gen = ++docsLoadGen.current;
    setClusterDocs([]);
    setCheckedDocIds([]);
    setClusterInfo(null);
    if (!bucketId) return;
    setDocsLoading(true);
    fetchEvalClusterDocuments(bucketId)
      .then(({ cluster, documents }) => {
        if (gen !== docsLoadGen.current) return;
        const wanted = pendingChecked.current;
        pendingChecked.current = null;
        setClusterInfo(cluster);
        setClusterDocs(documents);
        setCheckedDocIds(
          documents
            .filter((d) => !wanted || wanted.includes(d.evidence_id))
            .map((d) => d.evidence_id),
        );
      })
      .catch((e) => {
        if (gen === docsLoadGen.current) toast(formatApiError(e), true);
      })
      .finally(() => {
        if (gen === docsLoadGen.current) setDocsLoading(false);
      });
  }, [bucketId, toast]);

  useEffect(() => {
    if (!prefsHydrated.current) return;
    persistPrefs();
  }, [selectedModels, bucketId, activeRunId, persistPrefs]);

  useEffect(() => {
    if (!pickerModel && availableToAdd.length) {
      setPickerModel(availableToAdd[0].id);
    }
  }, [availableToAdd, pickerModel]);

  const removeModel = (id: string) => {
    setSelectedModels((prev) => prev.filter((m) => m !== id));
  };

  const addModelFromPicker = () => {
    if (!pickerModel) {
      toast("Choose a model from the list", true);
      return;
    }
    if (selectedModels.includes(pickerModel)) {
      toast("Model already in the arena", true);
      return;
    }
    if (activeModels.length >= 6) {
      toast("Maximum 6 models per run", true);
      return;
    }
    setSelectedModels((prev) => [...prev, pickerModel]);
    const next = availableToAdd.find((m) => m.id !== pickerModel);
    setPickerModel(next?.id || "");
  };

  const applySavedRun = useCallback(
    (run: SavedEvalRun, opts?: { focusResults?: boolean }) => {
      setActiveRunId(run.id);
      setSelectedModels(run.models);
      if (run.results?.length) {
        setResponse(savedRunToResponse(run));
      }
      // Loading a run restores its inputs, prompts included: re-running it repeats the experiment.
      const runPrompts = run.evidence_preview?.prompts;
      if (runPrompts) {
        updatePromptSandbox(
          Object.fromEntries(
            Object.entries(runPrompts)
              .filter(([, used]) => used.source === "run")
              .map(([kind, used]) => [kind, used.text]),
          ),
        );
      }
      const runCluster = run.evidence_preview?.cluster;
      if (runCluster?.bucket_id) {
        selectCluster(
          runCluster.bucket_id,
          (run.evidence_preview?.documents || []).map((d) => d.evidence_id),
        );
      }
      const runProfile = normalizePdrProfile(run.pdr_schema_profile || pdrSchemaProfile);
      if (run.pdr_schema_profile) {
        setPdrSchemaProfile(runProfile);
      }
      if (run.pdr_context?.trim()) {
        applyPdrText(runProfile, run.pdr_context, { saved: true });
        saveEvalStandalonePdr(runProfile, run.pdr_context, { explicitlySaved: true });
      } else if (run.existing_pdr) {
        const json = JSON.stringify(run.existing_pdr, null, 2);
        applyPdrText(runProfile, json, { saved: true });
      }
      const focus = opts?.focusResults ?? true;
      setResultsFocusMode(focus);
      persistPrefs({ models: run.models, lastRunId: run.id, resultsFocusMode: focus });
    },
    [applyPdrText, persistPrefs, selectCluster, updatePromptSandbox],
  );

  const loadSavedRun = async (run: SavedEvalRun) => {
    try {
      let full = run;
      if (!run.results?.length) {
        full = await fetchEvalRunDetail(run.id);
        setHistory((prev) => {
          const next = prev.map((r) => (r.id === full.id ? { ...r, ...full } : r));
          if (!next.some((r) => r.id === full.id)) return [full, ...prev];
          return next;
        });
      }
      applySavedRun(full);
      setRunsPanelOpen(false);
    } catch (e) {
      toast(formatApiError(e), true);
    }
  };

  const handleDeleteRun = (runId: string, e: MouseEvent) => {
    e.stopPropagation();
    void deleteEvalRunRemote(runId);
    deleteEvalRun(runId);
    const next = loadEvalHistory();
    setHistory(next);
    if (activeRunId === runId) {
      setActiveRunId("");
      setResponse(null);
      setResultsFocusMode(false);
    }
    toast("Run removed from history");
  };

  /** Present a finished run the way the page always has: table, history entry, focus mode. */
  const finishRun = async (data: EvalResponse) => {
    setResponse(data);
    const saved: SavedEvalRun = {
      id: data.run_id || crypto.randomUUID(),
      saved_at: new Date().toISOString(),
      evidence_id: data.evidence_id,
      project_id: data.project_id || "",
      include_reasoning: true,
      models: data.results.map((r) => r.model),
      pdr_context: data.pdr_context || pdrContextText,
      pdr_schema_profile: data.pdr_schema_profile || pdrSchemaProfile,
      evidence_preview: data.evidence_preview,
      project_preview: data.project_preview,
      results: data.results,
    };
    upsertEvalRun(saved);
    const runs = await refreshHistory();
    if (!runs.some((r) => r.id === saved.id)) {
      setHistory((prev) => [saved, ...prev]);
    }
    setActiveRunId(saved.id);
    setTableExpanded(true);
    setResultsFocusMode(true);
    persistPrefs({
      lastRunId: saved.id,
      resultsFocusMode: true,
      tableExpanded: true,
      runningJobId: "",
    });
    const prof = normalizePdrProfile(data.pdr_schema_profile || pdrSchemaProfile);
    const label = PDR_PROFILE_OPTIONS.find((o) => o.id === prof)?.label || prof;
    const calls = data.evidence_preview?.coverage?.calls_per_model ?? 1;
    toast(
      `Evaluation complete (${data.results?.length ?? 0} models × ${calls} call${
        calls === 1 ? "" : "s"
      }) · ${label} schema — saved to history`,
    );
  };

  const endRun = () => {
    setRunningJobId("");
    setJobModels([]);
    setPartialResults([]);
    setRunStartedAt(0);
    persistPrefs({ runningJobId: "" });
  };

  // The poll loop outlives many renders; it always calls the latest handlers.
  const finishRunRef = useRef(finishRun);
  finishRunRef.current = finishRun;
  const endRunRef = useRef(endRun);
  endRunRef.current = endRun;

  useEffect(() => {
    if (!runningJobId) return;
    let stopped = false;
    let failures = 0;
    let timer = 0;
    const tick = async () => {
      try {
        const job = await fetchEvalJob<EvalResponse>(runningJobId);
        if (stopped) return;
        failures = 0;
        const record = job.job_record;
        if (record?.payload?.models?.length) {
          const models = record.payload.models;
          setJobModels((prev) => (prev.length ? prev : models));
        }
        if (record?.created_at) {
          const created = Date.parse(record.created_at);
          setRunStartedAt((prev) => prev || created);
        }
        if (job.status === "ok") {
          stopped = true;
          endRunRef.current();
          if (job.result?.eval) await finishRunRef.current(job.result.eval);
          else toast("Evaluation finished without results", true);
          return;
        }
        if (EVAL_JOB_FAILED.has(job.status)) {
          stopped = true;
          endRunRef.current();
          toast(`Evaluation ${job.status}: ${job.error || "no details"}`, true);
          return;
        }
        setPartialResults(job.result?.partial?.results ?? []);
      } catch (e) {
        failures += 1;
        if (failures >= EVAL_POLL_MAX_FAILURES) {
          stopped = true;
          endRunRef.current();
          toast(`Lost track of the evaluation: ${formatApiError(e)}. It may still appear under Runs.`, true);
          return;
        }
      }
      if (!stopped) timer = window.setTimeout(() => void tick(), EVAL_POLL_MS);
    };
    timer = window.setTimeout(() => void tick(), 1_000);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [runningJobId, toast]);

  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [running]);

  const runEvaluation = async () => {
    if (!bucketId) {
      toast("Pick a vendor cluster", true);
      return;
    }
    if (checkedDocIds.length === 0) {
      toast("Tick at least one document", true);
      return;
    }
    if (activeModels.length === 0) {
      toast("Add at least one model", true);
      return;
    }
    if (!pdrContextText.trim()) {
      toast("Add eval PDR context in the sandbox above", true);
      return;
    }
    const brokenPrompt = evalPromptTemplates.find(
      (t) => evalSandbox[t.kind] !== undefined && missingTokens(evalSandbox[t.kind], t.required).length,
    );
    if (brokenPrompt) {
      toast(
        `Synthesis prompt sandbox: "${brokenPrompt.label}" is missing ${missingTokens(
          evalSandbox[brokenPrompt.kind],
          brokenPrompt.required,
        ).join(", ")}`,
        true,
      );
      setPromptCollapsed(false);
      return;
    }
    setResponse(null);
    setActiveRunId("");
    setPartialResults([]);
    try {
      const job = await startEvalRun({
        bucket_id: bucketId,
        evidence_ids: checkedDocIds,
        project_id: "",
        models: activeModels,
        include_reasoning: EVAL_INCLUDE_REASONING,
        pdr_context: pdrContextText,
        pdr_schema_profile: pdrSchemaProfile,
        prompts: evalSandbox,
      });
      setJobModels(job.models);
      setRunStartedAt(Date.now());
      setRunningJobId(job.job_id);
      persistPrefs({ runningJobId: job.job_id });
    } catch (e) {
      toast(formatApiError(e), true);
    }
  };

  const results = response?.results || [];
  const showResultsOnly = resultsFocusMode && Boolean(response?.results?.length);

  const runsButton = (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => setRunsPanelOpen((open) => !open)}
      aria-expanded={runsPanelOpen}
      aria-controls="eval-runs-panel"
      className={runsPanelOpen ? "text-primary bg-primary/10" : undefined}
    >
      {running ? <span className="orion-spinner" aria-hidden /> : null}
      <Icon name="history" className="text-base" />
      Runs
      {history.length > 0 ? (
        <span className="text-primary normal-case font-data-mono">({history.length})</span>
      ) : null}
    </Button>
  );

  const orderedPartial = useMemo(() => {
    const order = new Map(jobModels.map((m, i) => [m, i]));
    return [...partialResults].sort(
      (a, b) => (order.get(a.model) ?? 99) - (order.get(b.model) ?? 99),
    );
  }, [partialResults, jobModels]);

  const promptEditCount = useMemo(
    () =>
      Object.entries(evalSandbox).filter(
        ([kind, text]) => text !== evalPromptTemplates.find((t) => t.kind === kind)?.content,
      ).length,
    [evalSandbox, evalPromptTemplates],
  );

  const selectedCluster = useMemo(
    () => clusterInfo ?? clusters.find((c) => c.bucket_id === bucketId) ?? null,
    [clusterInfo, clusters, bucketId],
  );

  const responseCoverage = coverageText(response?.evidence_preview?.coverage);
  const responseEditedPrompts = Object.entries(response?.evidence_preview?.prompts ?? {})
    .filter(([, used]) => used.source === "run")
    .map(([kind]) => promptTemplates.find((t) => t.kind === kind)?.label ?? kind);
  const responsePromptNote = responseEditedPrompts.length
    ? ` · sandbox prompt: ${responseEditedPrompts.join(", ")}`
    : "";

  const resultsSummary = response ? (
    <span className="text-body-sm text-on-surface-variant truncate">
      {response.evidence_preview?.title || response.evidence_id}
      {response.project_preview?.project_name ? (
        <>
          {" "}
          · {response.project_preview.project_name}
          {response.project_preview.target_company
            ? ` (${response.project_preview.target_company})`
            : ""}
        </>
      ) : null}
      {responseCoverage ? ` · ${responseCoverage}` : null}
      {responsePromptNote}
    </span>
  ) : null;

  return (
    <div className="flex flex-col h-full min-h-0 bg-background">
      <Drawer
        id="eval-runs-panel"
        icon="history"
        open={runsPanelOpen}
        onClose={() => setRunsPanelOpen(false)}
        title="Past evaluation runs"
        subtitle={
          history.length > 0
            ? `${history.length} saved run${history.length === 1 ? "" : "s"}`
            : "Complete an evaluation to save runs here"
        }
      >
        <EvalRunHistoryPanel
          variant="drawer"
          runs={history}
          activeRunId={activeRunId}
          loading={historyLoading}
          onSelect={(run) => void loadSavedRun(run)}
          onDelete={handleDeleteRun}
        />
      </Drawer>

      {!showResultsOnly ? (
        <PageTitle
          title="LLM Synthesis Evaluation Playground"
          meta="Standalone eval PDR + a vendor cluster's documents in; Phase 3 JSON out. Concurrent models, side-by-side compare."
          actions={runsButton}
        />
      ) : null}

      {!showResultsOnly ? (
      <EvalPdrSandbox
        activeProfile={pdrSchemaProfile}
        onProfileChange={persistPdrProfile}
        text={pdrContextText}
        dirty={pdrDirty}
        loading={pdrLoading}
        collapsed={pdrCollapsed}
        onToggleCollapsed={() => setPdrCollapsed((c) => !c)}
        onTextChange={handlePdrContextChange}
        onSave={saveSandboxPdr}
        onInsertJsonSkeleton={insertPdrSkeleton}
        importProjectId={importProjectId}
        importProjectOptions={importProjectOptions}
        onImportProjectChange={setImportProjectId}
        onImportFromProject={() => void importPdrFromProject()}
        importLoading={pdrLoading}
      />
      ) : null}

      {!showResultsOnly ? (
        <section className="border-b border-outline-variant bg-surface flex flex-col shrink-0">
          <div className="px-6 py-3 flex items-center justify-between gap-3 border-b border-outline-variant/50">
            <div className="flex items-center gap-3 min-w-0 flex-wrap">
              <span className="font-data-label text-data-label uppercase text-on-surface-variant">
                Synthesis prompt (sandbox)
              </span>
              <span className="text-body-sm text-on-surface-variant truncate">
                Master prompt + instructions every model receives
              </span>
              {promptEditCount ? (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase shrink-0 bg-tertiary/10 text-tertiary border-tertiary/20">
                  {promptEditCount} edited for your runs
                </span>
              ) : (
                <span className="text-[9px] font-data-mono uppercase text-on-surface-variant">
                  Same as production
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPromptCollapsed((c) => !c)}
              className="uppercase font-data-label text-data-label shrink-0"
            >
              <Icon name={promptCollapsed ? "expand_more" : "expand_less"} />
              {promptCollapsed ? "Expand" : "Collapse"}
            </Button>
          </div>
          {!promptCollapsed ? (
            <SynthesisPromptEditor
              mode="sandbox"
              templates={evalPromptTemplates}
              loading={promptLoading}
              onTemplatesChange={setPromptTemplates}
              sandbox={promptSandbox}
              onSandboxChange={updatePromptSandbox}
              targetCompany={selectedCluster?.target_company ?? ""}
              pdrProfile={pdrSchemaProfile}
              className="max-h-[70vh]"
            />
          ) : null}
        </section>
      ) : null}

      {!showResultsOnly ? (
      <div className="px-4 py-4 bg-surface-container border-b border-outline-variant flex flex-wrap items-end gap-4 shrink-0">
        <div className="flex-1 min-w-[280px]">
          <VendorClusterPicker
            clusters={clusters}
            clustersLoading={clustersLoading}
            cluster={selectedCluster}
            onSelect={(id) => selectCluster(id)}
            documents={clusterDocs}
            documentsLoading={docsLoading}
            checkedIds={checkedDocIds}
            onCheckedChange={setCheckedDocIds}
            modelCount={activeModels.length}
            maxCallsPerModel={maxCallsPerModel}
          />
        </div>
        <Button
          variant="primary"
          disabled={running || docsLoading || checkedDocIds.length === 0}
          onClick={() => void runEvaluation()}
        >
          {running && <span className="orion-spinner" />}
          Run concurrent evaluation
        </Button>
      </div>
      ) : null}

      {!showResultsOnly ? (
      <div className="px-4 py-3 border-b border-outline-variant bg-surface-container-lowest shrink-0 space-y-3">
        <p className="font-data-label text-data-label uppercase text-on-surface-variant">
          Models in arena ({activeModels.length}/6)
        </p>

        <div className="flex flex-wrap gap-2 items-center min-h-[32px]">
          {activeModels.length === 0 ? (
            <span className="text-body-sm text-on-surface-variant">No models selected.</span>
          ) : (
            activeModels.map((id) => (
              <span
                key={id}
                className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded border border-primary/30 bg-primary/10 text-primary text-body-sm"
              >
                <span className="max-w-[200px] truncate" title={id}>
                  {modelLabel(id, catalog)}
                </span>
                <Button
                  variant="unstyled"
                  size="icon-sm"
                  onClick={() => removeModel(id)}
                  className="rounded hover:bg-primary/20 text-primary"
                  title="Remove model"
                  aria-label={`Remove ${id}`}
                >
                  <Icon name="close" className="text-sm" />
                </Button>
              </span>
            ))
          )}
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          {catalog.length > 0 ? (
            <>
              <Select
                value={pickerModel}
                onChange={(e) => setPickerModel(e.target.value)}
                disabled={availableToAdd.length === 0 || activeModels.length >= 6}
                className="min-w-[220px] max-w-full"
              >
                {availableToAdd.length === 0 ? (
                  <option value="">All catalog models added</option>
                ) : (
                  availableToAdd.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name || m.id}
                      {m.prompt_per_million ? ` ($${m.prompt_per_million}/M)` : ""}
                    </option>
                  ))
                )}
              </Select>
              <Button
                variant="outline-primary"
                size="sm"
                disabled={
                  !pickerModel || activeModels.length >= 6 || availableToAdd.length === 0
                }
                onClick={addModelFromPicker}
              >
                + Add model
              </Button>
              {activeModels.length >= 6 ? (
                <span className="text-[10px] text-on-surface-variant uppercase tracking-wide">
                  Max 6 models
                </span>
              ) : null}
            </>
          ) : (
            <span className="text-on-surface-variant text-body-sm">Loading model catalog…</span>
          )}
        </div>
      </div>
      ) : null}

      {showResultsOnly ? (
        <div className="px-4 py-2 border-b border-outline-variant bg-surface-container-high/90 shrink-0 sticky top-0 z-20 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3 min-w-0 flex-1">
            {resultsSummary}
            {response?.results?.length ? (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded border uppercase shrink-0 bg-primary/10 text-primary border-primary/30">
                {resultsSchemaProfile === PDR_PROFILE_COMPLETE ? "Complete" : "Slim"} schema
              </span>
            ) : null}
            {activeRunId ? (
              <span className="text-[10px] font-data-mono uppercase text-tertiary">
                Saved · {formatRunWhen(activeSavedRun?.saved_at || "")}
              </span>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            {runsButton}
            {response?.results?.length ? (
              <EvalTableExpandToggle expanded={tableExpanded} onToggle={toggleTableExpanded} />
            ) : null}
            {activePdrContext ? (
              <Button
                variant="link"
                size="sm"
                onClick={() => setPdrSentOpen((o) => !o)}
                className="inline-flex items-center gap-1 px-2 py-1.5"
              >
                <Icon name="description" className="text-base" />
                PDR sent {pdrSentOpen ? "▴" : "▾"}
              </Button>
            ) : null}
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => {
                setResultsFocusMode(false);
                persistPrefs({ resultsFocusMode: false });
              }}
            >
              <Icon name="tune" className="text-base" />
              Show setup
            </Button>
          </div>
        </div>
      ) : null}

      {schemaMismatch ? (
        <div className="px-4 py-2 bg-tertiary/10 border-b border-tertiary/30 text-body-sm text-on-surface shrink-0">
          These results used the{" "}
          <strong>{resultsSchemaProfile === PDR_PROFILE_COMPLETE ? "Complete" : "Slim"}</strong>{" "}
          schema. The sandbox is on{" "}
          <strong>{pdrSchemaProfile === PDR_PROFILE_COMPLETE ? "Complete" : "Slim"}</strong> — switch
          tabs to match, or re-run on Complete with{" "}
          <strong>Insert JSON skeleton</strong> to fill Company Type, CTPP, etc.
        </div>
      ) : null}

      {showResultsOnly && pdrSentOpen && activePdrContext ? (
        <div className="shrink-0 border-b border-outline-variant bg-surface-container-lowest max-h-48 overflow-auto custom-scrollbar">
          <pre className="p-4 text-xs font-data-mono text-on-surface/90 whitespace-pre-wrap">
            {activePdrContext}
          </pre>
        </div>
      ) : null}

      {!showResultsOnly && response?.evidence_preview ? (
        <div className="px-4 py-2 text-body-sm text-on-surface-variant border-b border-outline-variant shrink-0 flex flex-wrap gap-2 items-center">
          <span>
            {response.evidence_preview.cluster ? "Vendor cluster" : "Evidence"}:{" "}
            {response.evidence_preview.title || response.evidence_id}
            {response.project_preview?.project_name && (
              <>
                {" "}
                · Project: {response.project_preview.project_name} (
                {response.project_preview.target_company})
              </>
            )}
            {responseCoverage ? ` · ${responseCoverage}` : null}
            {responsePromptNote}
          </span>
          {pdrDirty ? (
            <span className="text-[10px] font-data-mono uppercase text-tertiary">
              Sandbox PDR (modified)
            </span>
          ) : null}
          {activeRunId ? (
            <span className="text-[10px] font-data-mono uppercase text-tertiary">
              Saved run · {formatRunWhen(activeSavedRun?.saved_at || "")}
            </span>
          ) : null}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {response.results?.length ? (
              <EvalTableExpandToggle
                expanded={tableExpanded}
                onToggle={toggleTableExpanded}
              />
            ) : null}
            {response.results?.length ? (
              <Button
                variant="link"
                size="sm"
                onClick={() => {
                  setResultsFocusMode(true);
                  persistPrefs({ resultsFocusMode: true });
                }}
                className="inline-flex items-center gap-1"
              >
                <Icon name="table_chart" className="text-base" />
                Table only
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      <div
        className={`flex-1 min-h-0 custom-scrollbar ${tableExpanded && response?.results?.length ? "overflow-x-auto overflow-y-auto" : "overflow-auto"}`}
      >
        {!response && !running && (
          <div className="p-8 text-center text-on-surface-variant">
            <Icon name="science" className="text-4xl mb-2 opacity-50" />
            <p>Pick a vendor cluster, tick its documents, then run a concurrent evaluation.</p>
            {history.length > 0 ? (
              <p className="text-sm mt-2">
                Or open <strong className="text-on-surface">Saved runs</strong> above to review a
                past benchmark.
              </p>
            ) : null}
          </div>
        )}
        {running ? (
          <>
            <EvalRunProgress
              models={jobModels.length ? jobModels : activeModels}
              finished={partialResults}
              startedAt={runStartedAt}
              now={now}
              catalog={catalog}
            />
            {orderedPartial.length > 0 ? (
              <EvalResultsTable
                results={orderedPartial}
                catalog={catalog}
                expanded={tableExpanded}
                pdrProfile={pdrSchemaProfile}
              />
            ) : (
              <EvalResultsLoading modelCount={jobModels.length || activeModels.length} />
            )}
          </>
        ) : null}
        {!running && response && results.length > 0 && (
          <EvalResultsTable
            results={results}
            catalog={catalog}
            expanded={tableExpanded}
            pdrProfile={resultsSchemaProfile}
          />
        )}
      </div>
    </div>
  );
}
